import { calculatePercentageChangeFast, getUnixTime, last, trimDecimals } from '@einhaus/meteoric-helpers';
import { closeTradeTypes } from '../config/constants.js';
import { calculatePositionsPerDay } from '../tradingHelpers/calculatePositionsPerDay.js';
import { calculateRiskCapitalStats } from '../tradingHelpers/calculateRiskCapitalStats.js';
import { checkTradeGroupForCompletion } from '../tradingHelpers/checkTradeGroupForCompletion.js';
const hasFiniteBacktestEconomics = (trade) => typeof trade.profit === 'number' &&
    Number.isFinite(trade.profit) &&
    typeof trade.profitPercentage === 'number' &&
    Number.isFinite(trade.profitPercentage);
export const isEvaluatedBacktestTrade = (trade) => hasFiniteBacktestEconomics(trade);
export class SimulateBacktestTrades {
    startingBalance;
    balance;
    balanceCompounded;
    // TODO - can move this into the trade object?
    percentageGains = [];
    postTradeBalances = [];
    percentageGainsWithTimestamps = [];
    tradeSummaries = [];
    dateTimestamp = null;
    trades;
    numTrades = 0;
    numTradesLong = 0;
    numTradesShort = 0;
    totalFees = 0;
    direction;
    skipOverlappingTrades;
    constructor(config) {
        this.startingBalance = config.startingBalance;
        this.balance = config.startingBalance;
        this.balanceCompounded = config.startingBalance;
        this.dateTimestamp = config.date ? getUnixTime(config.date) : null;
        this.trades = config.trades;
        this.skipOverlappingTrades = config.skipOverlappingTrades ?? true;
        // For when we want to get the stats of just one direction of trades
        this.direction = config.direction;
    }
    simulate() {
        const { trades, dateTimestamp, direction, tradeSummaries, percentageGains, percentageGainsWithTimestamps, postTradeBalances } = this;
        // Iterate directly over each [tradeId, transactionsInGroup] pair
        for (const trade of trades) {
            if (!trade.transactions.length)
                continue;
            // Sort each group by timestamp (if needed)
            // (Might already be sorted, but if not, do it here)
            trade.transactions.sort((a, b) => a.timestamp - b.timestamp);
            const entryTrades = trade.transactions.filter((t) => t.isOpeningTrade);
            if (!entryTrades.length)
                continue;
            const entryTradesValue = entryTrades.reduce((acc, t) => acc + (t.value ?? 0), 0);
            const nonEntryTradesValue = trade.transactions.reduce((acc, t) => acc + (t.value ?? 0), 0);
            // TODO - Right now we're assuming the entry trade is representative of all entry trades.
            // TODO - If we ever have a bi-directional strategy, we'll need to change this.
            const entryTrade = entryTrades[0];
            // Check direction filter early
            if (direction && entryTrade.direction !== direction)
                continue;
            // Check date filter early
            if (dateTimestamp && entryTrade.timestamp >= dateTimestamp)
                continue;
            // For non-SellToOpen trades, ensure group is "complete"
            // (In your original code: if it's not SellToOpen and not complete -> skip)
            if ((entryTrade.derivativeType === 'Future' || entryTrade.type !== 'sellToOpen') &&
                !checkTradeGroupForCompletion(trade.transactions)) {
                continue;
            }
            if (entryTrade.derivativeType === 'Future' &&
                entryTrades.some((entry) => !Number.isFinite(entry.riskCapital) || entry.riskCapital <= 0 || entry.value !== 0)) {
                throw new Error('Evaluated futures require positive reserved capital and zero opening principal payment');
            }
            // Check overlapping trades early
            if (this.skipOverlappingTrades) {
                const didFindOverlappingTrade = tradeSummaries.find((summary) => entryTrade.timestamp >= summary.start &&
                    (entryTrade.derivativeType === 'Future'
                        ? entryTrade.timestamp < summary.end
                        : entryTrade.timestamp <= summary.end) &&
                    entryTrade.direction === summary.direction &&
                    entryTrade.entityId === summary.entityId &&
                    entryTrade.entityType === summary.entityType);
                if (didFindOverlappingTrade)
                    continue;
            }
            if (!hasFiniteBacktestEconomics(trade)) {
                throw new Error(`Completed backtest trade ${trade.id} is missing canonical evaluation economics`);
            }
            // Count trades
            if (entryTrade.direction === 'long')
                this.numTradesLong++;
            if (entryTrade.direction === 'short')
                this.numTradesShort++;
            this.numTrades++;
            const tradingFees = trade.transactions.reduce((total, transaction) => total + transaction.fees, 0);
            if (!Number.isFinite(tradingFees) || tradingFees < 0) {
                throw new Error(`Completed backtest trade ${trade.id} has invalid canonical transaction fees`);
            }
            this.totalFees += tradingFees;
            this.balance += trade.profit;
            percentageGains.push({
                direction: entryTrade.direction,
                percentage: trade.profitPercentage
            });
            percentageGainsWithTimestamps.push({
                direction: entryTrade.direction,
                timestamp: entryTrade.timestamp,
                percentageGain: trade.profitPercentage
            });
            // Identify closing trades
            const closingTrades = trade.transactions.filter((t) => closeTradeTypes.includes(t.type));
            // If these fields are absolutely required, we skip if not present
            if (!entryTrade.riskCapital || (entryTrade.derivativeType !== 'Future' && !entryTrade.value)) {
                continue;
            }
            tradeSummaries.push({
                id: String(trade.id),
                start: entryTrade.timestamp,
                end: last(closingTrades)?.timestamp ?? entryTrade.derivativeExpirationTimestamp ?? entryTrade.timestamp,
                direction: entryTrade.direction,
                entityId: entryTrade.entityId ?? 0,
                entityType: entryTrade.entityType ?? 'Asset',
                riskCapital: entryTrade.riskCapital ?? 0,
                profit: trade.profit,
                openingValue: entryTradesValue,
                closingValue: nonEntryTradesValue,
                openingTxnType: entryTrade.type
            });
            // Post-trade balance
            postTradeBalances.push({
                balance: trimDecimals(this.balance),
                timestamp: entryTrade.timestamp
            });
        }
    }
    getStats() {
        const profit = trimDecimals(this.balance - this.startingBalance);
        const profitPercentage = calculatePercentageChangeFast(this.startingBalance, this.balance);
        const { returnOnRiskCapital, averageProfitPerDay, averageRiskCapitalPerDay, avgProfitPerDayPercent, medianProfitPerDayPercent } = calculateRiskCapitalStats(this.tradeSummaries);
        return {
            profit,
            profitPercentage: trimDecimals(profitPercentage),
            numberOfTrades: this.numTrades,
            numTradesLong: this.numTradesLong,
            numTradesShort: this.numTradesShort,
            tradingFees: trimDecimals(this.totalFees),
            percentageGains: this.percentageGains,
            percentageGainsWithTimestamps: this.percentageGainsWithTimestamps,
            tradeSummaries: this.tradeSummaries,
            postTradeBalances: this.postTradeBalances,
            startingBalance: trimDecimals(this.startingBalance),
            endingBalance: trimDecimals(this.balance),
            avgTradeProfit: trimDecimals(profit / this.numTrades),
            avgTradeProfitPercentage: trimDecimals(profitPercentage / this.numTrades),
            returnOnRiskCapital,
            averageProfitPerDay,
            averageRiskCapitalPerDay,
            avgProfitPerDayPercent,
            medianProfitPerDayPercent,
            averagePositions: calculatePositionsPerDay(this.tradeSummaries)
        };
    }
}
//# sourceMappingURL=SimulateBacktestTrades.js.map